<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
    <div class="max-w-md w-full text-center">
      <p class="text-sm font-semibold text-red-600">Error</p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
        Something went wrong
      </h1>
      <p class="mt-4 text-base text-gray-600">
        {{ errorMessage || 'An unexpected error occurred. Please try again or contact support if the problem persists.' }}
      </p>

      <dl class="mt-8 rounded-md border border-gray-200 bg-white p-4 text-left text-sm space-y-2">
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500">Error code</dt>
          <dd class="font-mono text-gray-900">{{ errorCode || 'UNKNOWN' }}</dd>
        </div>
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500">Time</dt>
          <dd class="text-gray-900">{{ timestamp }}</dd>
        </div>
      </dl>

      <div class="mt-6 flex flex-col sm:flex-row justify-center gap-3">
        <Button variant="primary" @click="retryConnection">Reload page</Button>
        <Button variant="outline" @click="reportIssue">Report issue</Button>
      </div>

      <p class="mt-8 text-xs text-gray-500">
        Need help?
        <a :href="`mailto:${supportEmail}`" class="text-indigo-600 hover:text-indigo-500">
          {{ supportEmail }}
        </a>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { Button } from '@asabdev/asab-ui';

  interface Props {
    errorCode?: string;
    errorMessage?: string;
  }

  const props = defineProps<Props>();

  const supportEmail = (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) || 'support@asab.dev';
  const timestamp = computed(() => new Date().toISOString());

  function retryConnection() {
    window.location.reload();
  }

  function reportIssue() {
    const subject = encodeURIComponent(`ASAB Error Report — ${props.errorCode || 'UNKNOWN'}`);
    const body = encodeURIComponent(
      [
        `Error code: ${props.errorCode || 'UNKNOWN'}`,
        `Message: ${props.errorMessage || 'Unspecified error'}`,
        `Timestamp: ${new Date().toISOString()}`,
        `URL: ${window.location.href}`,
        `User-Agent: ${navigator.userAgent}`,
        '',
        'What were you doing when this happened?',
        '',
      ].join('\n'),
    );
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  }

  onMounted(() => {
    console.error('ASAB Error:', {
      code: props.errorCode,
      message: props.errorMessage,
      timestamp: timestamp.value,
      url: window.location.href,
    });
  });
</script>
